import { Typography, message } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import pauseIconDark from "../../assets/icons/grey-pause.svg";
import playIconDark from "../../assets/icons/grey-play.svg";
import microIcon from "../../assets/icons/micro.svg";
import pauseIcon from "../../assets/icons/pause-cercle.svg";
import resumeIcon from "../../assets/icons/resume-recording.svg";
import { useAudioWaveform } from "../../hooks/useAudioWaveform";
import { storageService } from "../../services";
import { quickFileService } from "../../services/quickFile.service";
import Loader from "../loader/Loader";
import "./index.less";

// Optimized merge function for combining multiple audio segments
async function mergeAudioBlobs(blobs: Blob[]): Promise<Blob> {
  if (blobs.length === 1) {
    return blobs[0];
  }

  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    const decodePromises = blobs.map(async (blob) => {
      const arrayBuffer = await blob.arrayBuffer();
      return audioContext.decodeAudioData(arrayBuffer);
    });

    const audioBuffers = await Promise.all(decodePromises);

    const totalLength = audioBuffers.reduce((sum, buffer) => sum + buffer.length, 0);
    const numberOfChannels = audioBuffers[0].numberOfChannels;
    const sampleRate = audioBuffers[0].sampleRate;

    const offlineContext = new OfflineAudioContext(numberOfChannels, totalLength, sampleRate);

    let startTime = 0;
    audioBuffers.forEach((buffer) => {
      const source = offlineContext.createBufferSource();
      source.buffer = buffer;
      source.connect(offlineContext.destination);
      source.start(startTime);
      startTime += buffer.duration;
    });

    const mergedBuffer = await offlineContext.startRendering();

    const source = audioContext.createBufferSource();
    source.buffer = mergedBuffer;

    const destination = audioContext.createMediaStreamDestination();
    source.connect(destination);

    const recorder = new MediaRecorder(destination.stream, {
      mimeType: "audio/webm;codecs=opus",
      audioBitsPerSecond: 64000,
    } as any);

    const chunks: Blob[] = [];

    const result = await new Promise<Blob>((resolve, reject) => {
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        audioContext.close();
        resolve(new Blob(chunks, { type: "audio/webm;codecs=opus" }));
      };

      recorder.onerror = (err) => {
        audioContext.close();
        reject(err);
      };

      recorder.start(500);
      source.start(0);

      setTimeout(
        () => {
          try {
            source.stop();
          } catch (e) {}
          setTimeout(() => {
            try {
              recorder.stop();
            } catch (e) {}
          }, 100);
        },
        mergedBuffer.duration * 1000 + 300
      );
    });

    return result;
  } catch (error) {
    console.error("Error merging audio:", error);
    throw error;
  }
}

interface AudioRecorderProps {
  onAudioReady: (audioBlob: Blob, audioTitle: string, duration: number) => void;
  onCancel: () => void;
  maxDuration?: number; // in seconds, default 60
  audioTitle?: string; // Optional title for the audio recording
  isFromConv?: boolean;
  onUploadSuccess?: () => void; // Callback when upload is successful
  albumId?: string; // Album ID for uploading
  onOpenQuickAudioModals?: () => void; // Open quick-files audio selection modals
}

const AudioRecorderComponent: React.FC<AudioRecorderProps> = ({
  onAudioReady,
  onCancel,
  maxDuration = 60,
  audioTitle = "sending-audio",
  isFromConv,
  onUploadSuccess,
  albumId: albumIdProp,
  onOpenQuickAudioModals,
}) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isMergingAudio, setIsMergingAudio] = useState(false); // Loading state for merging

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const completedBlobsRef = useRef<Blob[]>([]); // Store completed recording segments
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [loadedData, setLoadedData] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [playbackProgress, setPlaybackProgress] = useState<number>(0); // 0-100
  const { albumId: albumIdFromRoute, conversationId } = useParams();
  const albumId = albumIdProp || albumIdFromRoute; // Use prop albumId if provided, otherwise use route param
  const navigate = useNavigate();

  const {
    waveformData,
    isLoading: isAnalyzing,
    cacheKey,
  } = useAudioWaveform(previewUrl || audioUrl, {
    segments: 120,
    scaleFactor: 10,
    fallbackToSimple: true,
  });

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [audioUrl, previewUrl]);

  useEffect(() => {
    if (audioRef.current && previewUrl) {
      audioRef.current.load();
      setPlaybackProgress(0);
      setIsPlaying(false);
    }
  }, [previewUrl]);

  const startRecording = async () => {
    try {
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch (e) {}
        setPreviewUrl(null);
      }
      setIsPaused(false);
      setAudioBlob(null);
      if (audioUrl) {
        try {
          URL.revokeObjectURL(audioUrl);
        } catch (e) {}
        setAudioUrl("");
      }

      // Clear all recording data when starting fresh
      chunksRef.current = [];
      completedBlobsRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm;codecs=opus" });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        // Stop all tracks to release microphone
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newTime;
        });
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      message.error(t("microphone-access-denied") || "Microphone access denied");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(true);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      setTimeout(async () => {
        const segmentBlob = new Blob(chunksRef.current, { type: "audio/webm;codecs=opus" });
        completedBlobsRef.current.push(segmentBlob);

        const quickPreviewUrl = URL.createObjectURL(segmentBlob);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(quickPreviewUrl);
        setAudioBlob(segmentBlob);

        if (completedBlobsRef.current.length > 1) {
          setIsMergingAudio(true);

          try {
            const mergedBlob = await mergeAudioBlobs(completedBlobsRef.current);
            const mergedUrl = URL.createObjectURL(mergedBlob);

            URL.revokeObjectURL(quickPreviewUrl);
            setPreviewUrl(mergedUrl);
            setAudioBlob(mergedBlob);
          } catch (error) {
            console.error("Error merging audio blobs:", error);
          } finally {
            setIsMergingAudio(false);
          }
        }
      }, 100);
    }
  };

  const resumeRecording = async () => {
    if (isPaused) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        streamRef.current = stream;
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: "audio/webm;codecs=opus",
        });

        mediaRecorderRef.current = mediaRecorder;

        chunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: "audio/webm;codecs=opus" });
          setAudioBlob(blob);
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);

          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
          }
        };

        mediaRecorder.start(100);
        setIsRecording(true);
        setIsPaused(false);

        recordingTimerRef.current = setInterval(() => {
          setRecordingTime((prev) => {
            const newTime = prev + 1;
            if (newTime >= maxDuration) {
              stopRecording();
              return maxDuration;
            }
            return newTime;
          });
        }, 1000);
      } catch (error) {
        console.error("Error resuming recording:", error);
        message.error(t("microphone-access-denied") || "Microphone access denied");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && (isRecording || isPaused)) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch (e) {}
        setPreviewUrl(null);
      }

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const playAudio = () => {
    if (audioRef.current && (previewUrl || audioUrl)) {
      setPlaybackProgress(0);
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const deleteRecording = () => {
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl("");
    }
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setRecordingTime(0);
    setIsPlaying(false);
    setIsPaused(false);
    chunksRef.current = [];
    completedBlobsRef.current = []; // Clear completed segments
  };

  const restartRecording = async () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    deleteRecording();
    await startRecording();
  };

  const sendAudio = async () => {
    let blobToSend: Blob | null = null;

    if (audioBlob) {
      blobToSend = audioBlob;
    } else if (completedBlobsRef.current.length > 1) {
      setIsMergingAudio(true);
      try {
        blobToSend = await mergeAudioBlobs(completedBlobsRef.current);
      } catch (error) {
        console.error("Error merging for send:", error);
        blobToSend = new Blob(completedBlobsRef.current, { type: "audio/webm;codecs=opus" });
      } finally {
        setIsMergingAudio(false);
      }
    } else if (completedBlobsRef.current.length === 1) {
      blobToSend = completedBlobsRef.current[0];
    } else if (isPaused && chunksRef.current.length > 0) {
      blobToSend = new Blob(chunksRef.current, { type: "audio/webm;codecs=opus" });
    }

    if (blobToSend && recordingTime > 0) {
      onAudioReady(blobToSend, audioTitle || "Untitled", recordingTime);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.includes("audio/")) {
        message.error(t("please-select-audio-file") || "Please select an audio file");
        return;
      }

      // Create audio element to get duration
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      audio.src = url;

      audio.onloadedmetadata = () => {
        const duration = Math.floor(audio.duration);
        URL.revokeObjectURL(url);

        // Convert file to blob and set it up for preview
        const blob = new Blob([file], { type: file.type });
        const previewUrl = URL.createObjectURL(blob);

        setAudioBlob(blob);
        setPreviewUrl(previewUrl);
        setRecordingTime(duration);
        setIsPaused(true); // Set to paused state to show preview controls
        setIsRecording(false);
      };

      audio.onerror = () => {
        URL.revokeObjectURL(url);
        message.error(t("failed-to-load-audio") || "Failed to load audio file");
      };
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  async function handleMediaUpload() {
    let blobToUpload: Blob | null = null;

    if (audioBlob) {
      blobToUpload = audioBlob;
    } else if (completedBlobsRef.current.length > 1) {
      setLoading(true);
      try {
        blobToUpload = await mergeAudioBlobs(completedBlobsRef.current);
      } catch (error) {
        console.error("Error merging for upload:", error);
        blobToUpload = new Blob(completedBlobsRef.current, { type: "audio/webm;codecs=opus" });
      }
      setLoading(false);
    } else if (completedBlobsRef.current.length === 1) {
      blobToUpload = completedBlobsRef.current[0];
    } else if (isPaused && chunksRef.current.length > 0) {
      blobToUpload = new Blob(chunksRef.current, { type: "audio/webm;codecs=opus" });
    }

    if (!blobToUpload) {
      message.error(t("no-audio-to-upload") || "No audio to upload");
      return;
    }

    const isImported = audioBlob && chunksRef.current.length === 0 && completedBlobsRef.current.length === 0;
    const fileName = isImported
      ? `audio_${new Date().getTime()}.${blobToUpload.type.split("/")[1].split(";")[0]}`
      : `audio_recording_${new Date().getTime()}.webm`;

    const audioFile = {
      name: fileName,
      type: blobToUpload.type || "audio/webm;codecs=opus",
      size: blobToUpload.size,
      originFileObj: blobToUpload,
      time: recordingTime,
    };

    try {
      setLoading(true);
      const result = await storageService.uploadFileMultiPart(audioFile, setLoadedData, blobToUpload.size, "quickFiles");

      const audioFiles: any[] = [];
      audioFiles.push({
        Location: result.Location,
        isPrivate: false,
        Key: result.Key,
        ETag: result.ETag,
        Bucket: result.Bucket,
        filename: audioFile.name,
        size: audioFile.size,
        title: audioTitle || "sending-audio",
        time: recordingTime,
      });

      const creatorId = localStorage.getItem("selectedCreator");
      const formData = new FormData();
      formData.append("audioFiles", JSON.stringify(audioFiles));
      formData.append("creatorId", creatorId as string);

      await quickFileService.addMediaToAlbum(albumId as string, formData, setProgress);

      message.success(t("audio-upload-success") || "Audio uploaded successfully");

      setLoading(false);
      deleteRecording();

      if (onUploadSuccess) {
        onUploadSuccess();
      }

      return result;
    } catch (error) {
      console.error("Error uploading audio:", error);
      message.error(t("audio-upload-failed") || "Failed to upload audio");
      setLoading(false);
    }
  }
  return (
    <div className="audio-recorder-container">
      <div className="recording-interface">
        {/* Show recording status only when actively recording */}
        {isRecording && !isPaused && (
          <div className="recording-status">
            <div className="recording-time">{formatTime(recordingTime)}</div>

            <div className="audio-wave-visualization">
              {Array(120)
                .fill(0)
                .map((_, i) => {
                  const randomHeight = Math.random() * 0.7 + 0.3;
                  const randomDelay = Math.random() * 0.8;

                  return (
                    <div
                      key={i}
                      className="wave-bar"
                      style={{
                        animationDelay: `${randomDelay}s`,
                        animationDuration: `${0.8 + Math.random() * 0.8}s`,
                        height: `${10 + randomHeight * 20}px`,
                        opacity: 0.7 + Math.random() * 0.3,
                        margin: "0 auto",
                      }}
                    />
                  );
                })}
            </div>
          </div>
        )}

        {/* Show preview controls only when paused (not when recording) */}
        {isPaused && (
          <div style={{ width: "100%" }}>
            {isMergingAudio ? (
              <div className="recording-status">
                <div className="audio-wave-visualization">
                  {Array(120)
                    .fill(0)
                    .map((_, i) => {
                      const randomHeight = Math.random() * 0.7 + 0.3;
                      const randomDelay = Math.random() * 0.8;

                      return (
                        <div
                          key={i}
                          className="wave-bar"
                          style={{
                            animationDelay: `${randomDelay}s`,
                            animationDuration: `${0.8 + Math.random() * 0.8}s`,
                            height: `${10 + randomHeight * 20}px`,
                            opacity: 0.7 + Math.random() * 0.3,
                            margin: "0 auto",
                          }}
                        />
                      );
                    })}
                </div>
              </div>
            ) : previewUrl ? (
              <>
                <div className="recording-status">
                  <div className="recording-time">{formatTime(recordingTime)}</div>
                  <div className="audio-preview-row">
                    <div className="audio-wave-visualization audio-bubble">
                      <div className="playback-buttons">
                        <img
                          src={isPlaying ? pauseIconDark : playIconDark}
                          onClick={isPlaying ? pauseAudio : playAudio}
                          className="play-button"
                          alt={isPlaying ? "Pause" : "Play"}
                        />
                      </div>
                      {(() => {
                        const data = waveformData && waveformData.length > 0 ? waveformData : Array(120).fill(0.5);
                        const totalBars = data.length;
                        const playedCount = Math.round((playbackProgress / 100) * totalBars);
                        return data.map((height, i) => {
                          const shouldAnimate = isAnalyzing;
                          const barHeight = shouldAnimate ? `${10 + (Math.random() * 0.7 + 0.3) * 20}px` : `${10 + height * 20}px`;

                          return (
                            <div
                              key={i}
                              className={`wave-bar ${shouldAnimate ? "animated" : "static"} ${i < playedCount ? "played" : ""}`}
                              style={{
                                height: barHeight,
                                opacity: isAnalyzing ? 0.6 : 0.7,
                                margin: "0 auto",
                                transition: shouldAnimate ? "none" : "height 0.3s ease, opacity 0.3s ease",
                                animationDelay: shouldAnimate ? `${Math.random() * 0.8}s` : "0s",
                                animationDuration: shouldAnimate ? `${0.8 + Math.random() * 0.8}s` : "0s",
                              }}
                            />
                          );
                        });
                      })()}
                    </div>
                    <button className="resume-bubble" onClick={resumeRecording} title={t("resume-recording") || "Resume Recording"}>
                      <img src={resumeIcon} alt="Resume" />
                    </button>
                  </div>
                </div>
                <audio
                  ref={audioRef}
                  src={previewUrl}
                  key={previewUrl} // Force re-render when URL changes
                  onTimeUpdate={(e) => {
                    const a = e.currentTarget as HTMLAudioElement;
                    if (a.duration > 0) setPlaybackProgress((a.currentTime / a.duration) * 100);
                  }}
                  onEnded={() => {
                    setIsPlaying(false);
                    setPlaybackProgress(100);
                  }}
                  style={{ display: "none" }}
                />
              </>
            ) : null}
          </div>
        )}

        <div className="recording-controls">
          {/* Initial state: show record and import buttons */}
          {!isRecording && !isPaused && (
            <>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 16,
                  marginTop: isFromConv ? -45 : 0,
                }}
              >
                {isFromConv && (
                  <div className="bottom-modal-header" style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Typography.Text className="bottom-modal-title f-30">{t("Message vocal")}</Typography.Text>
                  </div>
                )}

                {isFromConv && (
                  <button
                    className="bottom-modal-button mt-14"
                    onClick={() => {
                      if (onOpenQuickAudioModals) {
                        onOpenQuickAudioModals();
                        return;
                      }
                      if (conversationId) {
                        localStorage.setItem("quickFileActiveTab", "audio");
                        localStorage.setItem("quickFileAudioOnly", "true");
                        navigate(`/creator/quickLink?id=conversation&conversationId=${conversationId}`);
                      } else {
                        message.error(t("no-conversation-id-found") || "No conversationId found");
                      }
                    }}
                  >
                    <Typography.Text className="bottom-modal-button-title">{t("Quick-Files")}</Typography.Text>
                  </button>
                )}

                {isFromConv ? (
                  <button className="bottom-modal-button" onClick={startRecording}>
                    <Typography.Text className="bottom-modal-button-title">{t("record-now")}</Typography.Text>
                  </button>
                ) : (
                  <img
                    src={microIcon}
                    onClick={startRecording}
                    className="record-button"
                    alt="Start Recording"
                    style={{ width: 60, height: 60, cursor: "pointer" }}
                  />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.mp3,.m4a,.aac,.wav,.caf,.aif,.aiff,.flac,.ogg,.m4b,.webm"
                style={{ display: "none" }}
                onChange={handleFileSelected}
              />
            </>
          )}

          {isPaused && (
            <div className="audio-action-buttons">
              <button
                className="audio-action-button"
                onClick={() => {
                  if (!isFromConv) {
                    handleMediaUpload();
                  } else sendAudio();
                }}
                disabled={loading || isMergingAudio}
              >
                {loading ? (
                  `${t("audio-uploading")}... ${Math.round((loadedData / (chunksRef.current.reduce((acc, chunk) => acc + chunk.size, 0) || 1)) * 100)}%`
                ) : isMergingAudio ? (
                  <Loader size="small" inline />
                ) : (
                  t("validate")
                )}
              </button>
              <button className="audio-action-button cancel-button" onClick={restartRecording} disabled={loading || isMergingAudio}>
                {t("restart")}
              </button>
            </div>
          )}

          {/* Recording state: show pause button */}
          {isRecording && !isPaused && (
            <img src={pauseIcon} onClick={pauseRecording} className="pause-button" title={t("pause-recording") || "Pause Recording"} alt="Pause" />
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioRecorderComponent;
